// オンライン決済（UnivaPay）のボタン。カードと銀行振込。
//
// ■ カード番号はこの画面に入らない
// 入力から 3-D セキュアまで、すべて UnivaPay のフォームの中で完結する。
// こちらは金額と申込の情報を渡して開くだけ。
//
// ■ 支払い手段は **1 回に 1 つだけ** 渡す
// `paymentMethods` はカンマ区切りで複数も指定できるが、まとめて出すと
// 会員がどれを選んだのか画面側で分からず、案内（その場で終わるのか、
// あとから入金するのか）を出し分けられない。**手段ごとにボタンを出す。**
//
// ■ 銀行振込・コンビニは「開いた時点では未入金」
// UnivaPay が申込ごとに専用の振込先・支払い番号を発行し、**入金されてから**
// 支払い済みになる。その場では終わらないので、画面でもそう伝える。
//
// ■ 「支払えた」かどうかはサーバが決める
// ウィジェットの戻り値は当てにしない（ブラウザの言い分は書き換えられる）。
// 支払いが終わったら **サーバに探しに行かせる**。ウィジェットを閉じてしまった、
// 回線が切れた、といったときも同じ経路で拾える。
import { useEffect, useRef, useState } from "react";
import { api } from "../api";
import {
  UNIVAPAY_METHOD_ACTION,
  isDeferredMethod,
  normalizeUnivapayMethods,
  type UnivapayMethod,
} from "../../shared/univapay-methods";

const WIDGET_SRC = "https://widget.univapay.com/client/checkout.js";

interface CheckoutHandle {
  open: () => void;
  close?: () => void;
}
declare global {
  interface Window {
    UnivapayCheckout?: {
      create: (opts: Record<string, unknown>) => CheckoutHandle;
    };
  }
}

interface ConfigResp {
  appId: string;
  amount: number;
  currency: string;
  methods: UnivapayMethod[];
  applicationId: string;
  memberId: string;
}

/** ウィジェットの読み込みは 1 回だけ。押すたびに足さない。 */
let widgetLoad: Promise<void> | null = null;
function loadWidget(): Promise<void> {
  if (window.UnivapayCheckout) return Promise.resolve();
  if (widgetLoad) return widgetLoad;
  widgetLoad = new Promise<void>((resolve, reject) => {
    const el = document.createElement("script");
    el.src = WIDGET_SRC;
    el.async = true;
    el.onload = () => resolve();
    el.onerror = () => {
      // 次に押したときにやり直せるようにする
      widgetLoad = null;
      reject(new Error("決済フォームを読み込めませんでした"));
    };
    document.head.appendChild(el);
  });
  return widgetLoad;
}

/** 確認の間隔。短くしすぎると決済サービスの制限に近づく。 */
const POLL_MS = 6000;
const POLL_LIMIT = 30; // 約 3 分

export function UnivapayPayButtons({
  applicationId,
  onPaid,
}: {
  applicationId: string;
  onPaid: () => void;
}) {
  const [busy, setBusy] = useState<UnivapayMethod | null>(null);
  const [methods, setMethods] = useState<UnivapayMethod[]>(["card"]);
  // 画面に出す金額。請求額と同じ（参加費＋有料オプション）をサーバから受け取る
  const [amount, setAmount] = useState<number | null>(null);
  const [usedMethod, setUsedMethod] = useState<UnivapayMethod | null>(null);
  const [checking, setChecking] = useState(false);
  const [opened, setOpened] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const timer = useRef<number | null>(null);
  const tries = useRef(0);
  const alive = useRef(true);

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
      if (timer.current) window.clearTimeout(timer.current);
    };
  }, []);

  /** サーバに「この申込の支払いはあったか」を探させる。 */
  const check = async (silent: boolean): Promise<boolean> => {
    if (!silent) setChecking(true);
    try {
      const r = await api.post<{ status: string | null }>(
        "/payments/univapay/check",
        { applicationId },
      );
      if (r.status === "succeeded") {
        onPaid();
        return true;
      }
      if (!silent)
        setErr(
          r.status
            ? "決済を確認中です。少し待ってからもう一度お試しください。"
            : "まだ支払いが確認できていません。",
        );
      return false;
    } catch (e) {
      if (!silent) setErr((e as Error).message);
      return false;
    } finally {
      if (!silent) setChecking(false);
    }
  };

  /** 開いたあと、静かに確認し続ける（会員が何もしなくても反映されるように） */
  const startPolling = () => {
    if (timer.current) window.clearTimeout(timer.current);
    tries.current = 0;
    const tick = async () => {
      if (!alive.current) return;
      tries.current += 1;
      const done = await check(true);
      if (done || !alive.current || tries.current >= POLL_LIMIT) return;
      timer.current = window.setTimeout(tick, POLL_MS);
    };
    timer.current = window.setTimeout(tick, POLL_MS);
  };

  // どの手段を出すかはコミュニティの設定。押される前に一度だけ読む。
  useEffect(() => {
    let live = true;
    api
      .get<ConfigResp>(`/payments/univapay/config/${applicationId}`)
      .then((cfg) => {
        if (!live) return;
        setMethods(normalizeUnivapayMethods(cfg.methods));
        setAmount(cfg.amount);
      })
      // 読めなくてもボタンは出す（押した時点で改めて取るので、
      // ここで失敗を出すと「まだ何もしていないのにエラー」になる）。
      .catch(() => {});
    return () => {
      live = false;
    };
  }, [applicationId]);

  const pay = async (method: UnivapayMethod) => {
    setBusy(method);
    setErr(null);
    try {
      const [cfg] = await Promise.all([
        api.get<ConfigResp>(`/payments/univapay/config/${applicationId}`),
        loadWidget(),
      ]);
      if (!window.UnivapayCheckout)
        throw new Error("決済フォームを読み込めませんでした");

      const checkout = window.UnivapayCheckout.create({
        appId: cfg.appId,
        // トークンの作成から課金まで、決済フォームの中で完結させる。
        checkout: "payment",
        // **1 つだけ**渡す（複数指定の書き方が公式に示されていない）。
        paymentMethods: method,
        tokenType: "one_time",
        amount: cfg.amount,
        currency: cfg.currency.toLowerCase(),
        // あとで申込と突き合わせるための印。**サーバ側で必ず検算する**。
        metadata: {
          applicationId: cfg.applicationId,
          memberId: cfg.memberId,
        },
      });
      setUsedMethod(method);
      setOpened(true);
      startPolling();
      checkout.open();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-2">
        {methods.map((mth) => (
          <button
            key={mth}
            type="button"
            className={mth === "card" ? "btn-primary" : "btn-outline"}
            disabled={busy !== null}
            onClick={() => pay(mth)}
          >
            {busy === mth
              ? "準備中…"
              : `${UNIVAPAY_METHOD_ACTION[mth]}${
                  amount ? ` ¥${amount.toLocaleString()}` : ""
                }`}
          </button>
        ))}
      </div>

      {/* 確認ボタンは **いつでも** 出す。支払った後に画面を閉じた・再読み込みした場合でも、
          押せばサーバが UnivaPay から取り直して反映する（通知が届かなかったときの逃げ道） */}
      <div className="space-y-1">
        <p className="text-xs text-tora-ink/70">
          {!opened
            ? "UnivaPay でお支払い済みなのに反映されていないときは、下のボタンを押してください。"
            : usedMethod && isDeferredMethod(usedMethod)
              ? "表示されたお支払い先へお支払いください。入金が確認できると自動で反映されます（反映まで時間がかかることがあります）。この画面を閉じても申込は残ります。"
              : "支払いが終わると自動で反映されます。反映されないときは下のボタンを押してください。"}
        </p>
        <button
          type="button"
          className="btn-outline text-sm"
          disabled={checking}
          onClick={() => check(false)}
        >
          {checking ? "確認中…" : "支払い状況を確認"}
        </button>
      </div>

      {err && <p className="text-sm text-red-700">{err}</p>}
    </div>
  );
}
